// Data API functions (employees, tasks, etc.) - all requests go through MB
import { MAIN_BACKEND_URL } from './apiConfig';

export interface EmployeeCreate {
  name: string;
  surname: string;
  role?: string | null;
  external_id?: string | null;
}

export interface EmployeeCreateResponse {
  status: string;
  employee_id: number;
}

export interface Employee {
  id: number;
  name: string;
  surname: string;
  role: string | null;
  external_id: string | null;
  is_deleted?: boolean;
  created_at?: string;
}

export interface Task {
  id: number;
  task_code: string;
  quantity: number;
  location: string | null;
  employee_id: number;
  created_at: string;
  last_modified_at: string;
  is_deleted: boolean;
  forced_submit: boolean;
  comments: string | null;
  assignement_id?: number | null;
}

export interface TaskFilters {
  employee_id?: number[];
  date?: string[];
  include_deleted?: boolean;
}

export interface TaskDefinition {
  task_code: string;
  beschrijving: string;
  eenheid: string;
  hoeveelheid_resultaats_verplichting: string | null;
  prijs_per_eenheid_in_euro: number;
}

export interface TaskCreate {
  task_code: string;
  quantity: number;
  employee_id: number;
  location?: string | null;
  forced_submit?: boolean;
  comments?: string | null;
  assignement_id?: number | null;
}

export interface TaskCreateResponse {
  status: string;
  task_id: number;
}

export interface TaskUpdate {
  quantity?: number;
  location?: string | null;
  comments?: string | null;
}

export interface TaskUpdateResponse {
  status: string;
  task_id: number;
}

export interface ConversationCreate {
  employee_id: number | null;
  task_id: number | null;
  assignement_id?: number | null;
  conversation: Record<string, unknown>; // JSON-serializable: id, time, messages, etc.
}

export interface ConversationCreateResponse {
  status: string;
  conversation_id: number;
}

/** One row from GET /conversations/ (date + optional employee_id) */
export interface StoredConversationRow {
  id: number;
  employee_id: number | null;
  task_id: number | null;
  assignement_id?: number | null;
  created_at: string;
  conversation: Record<string, unknown>;
}

export interface AssignementRow {
  id: number;
  employee_id: number;
  address: string;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
  comments?: string | null;
}

export interface AssignementCreatePayload {
  employee_id: number;
  address: string;
}

export interface AssignementCreateResponse {
  status: string;
  assignement_id: number;
  assignement: AssignementRow;
}

/**
 * Serialize a conversation (messages + metadata) to a JSON-serializable object.
 * Converts Date fields to ISO strings so they can be stored in JSONB.
 */
export function serializeConversationForStorage(conversation: {
  id: string;
  time: Date;
  messages: Array<{
    id: string;
    text?: string;
    textKey?: string;
    sender: string;
    timestamp: Date;
    isAssistant: boolean;
    messageType?: string;
  }>;
  tasks?: unknown[];
  conversationDialogueState?: string;
  isSubmitted?: boolean;
  isCancelled?: boolean;
  assignementId?: number | null;
  assignementAddress?: string | null;
}): Record<string, unknown> {
  return {
    id: conversation.id,
    time: conversation.time instanceof Date ? conversation.time.toISOString() : conversation.time,
    messages: (conversation.messages || []).map((msg) => ({
      ...msg,
      timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
    })),
    tasks: conversation.tasks ?? [],
    conversationDialogueState: conversation.conversationDialogueState,
    isSubmitted: conversation.isSubmitted,
    isCancelled: conversation.isCancelled,
    assignementId: conversation.assignementId ?? null,
    assignementAddress: conversation.assignementAddress ?? null,
  };
}

export const sqlApi = {
  async addConversation(payload: ConversationCreate): Promise<ConversationCreateResponse> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/conversations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to save conversation' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving conversation:', error);
      throw error;
    }
  },

  async getConversations(params: {
    date: string; // YYYY-MM-DD
    employee_id?: number | null;
  }): Promise<StoredConversationRow[]> {
    try {
      const searchParams = new URLSearchParams({ date: params.date });
      if (params.employee_id != null) {
        searchParams.set('employee_id', String(params.employee_id));
      }
      const response = await fetch(
        `${MAIN_BACKEND_URL}/api/sql/conversations/?${searchParams.toString()}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch conversations' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  async getAssignements(params: { date: string; employee_id: number }): Promise<AssignementRow[]> {
    try {
      const searchParams = new URLSearchParams({
        date: params.date,
        employee_id: String(params.employee_id),
      });
      const response = await fetch(
        `${MAIN_BACKEND_URL}/api/sql/assignements/?${searchParams.toString()}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch assignements' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching assignements:', error);
      throw error;
    }
  },

  async createAssignement(payload: AssignementCreatePayload): Promise<AssignementCreateResponse> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/assignements/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create assignement' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error('Error creating assignement:', error);
      throw error;
    }
  },

  async updateAssignement(
    assignementId: number,
    payload: { address?: string; is_completed?: boolean; append_comment?: string }
  ): Promise<{ status: string; assignement: AssignementRow }> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/assignements/${assignementId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update assignement' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error('Error updating assignement:', error);
      throw error;
    }
  },

  async updateConversation(
    conversationId: number,
    conversation: Record<string, unknown>
  ): Promise<{ status: string }> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/conversations/${conversationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ conversation }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update conversation' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating conversation:', error);
      throw error;
    }
  },

  async addTask(taskData: TaskCreate): Promise<TaskCreateResponse> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/tasks/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create task' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  async getAllTaskDefinitions(): Promise<TaskDefinition[]> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/task_definitions/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch task definitions' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching task definitions:', error);
      throw error;
    }
  },

  async addEmployee(employeeData: EmployeeCreate): Promise<EmployeeCreateResponse> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/employees/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(employeeData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create employee' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  },

  async removeEmployee(employeeId: number): Promise<void> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/employees/${employeeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          // Handle both direct detail and nested error.detail formats
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.error?.detail) {
            errorMessage = errorData.error.detail;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch (parseError) {
          // If response is not JSON, try to get text
          try {
            const text = await response.text();
            if (text) {
              errorMessage = text;
            }
          } catch (textError) {
            // Keep default error message
            console.error('Could not parse error response:', parseError, textError);
          }
        }

        console.error(`Delete employee error (${response.status}):`, errorMessage);
        throw new Error(errorMessage);
      }

      // 204 No Content - successful deletion
      return;
    } catch (error) {
      console.error('Error deleting employee:', error);
      // Re-throw with more context if it's not already an Error with a message
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to delete employee: ${String(error)}`);
    }
  },

  async resetEmployeePassword(employeeId: number): Promise<void> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/employees/reset_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ employee_id: employeeId }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Employee not found');
        }
        const errorData = await response.json().catch(() => ({ detail: 'Failed to reset password' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  },

  async getAllEmployees(includeDeleted: boolean = false): Promise<Employee[]> {
    try {
      const url = `${MAIN_BACKEND_URL}/api/sql/employees/${includeDeleted ? '?include_deleted=true' : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch employees' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  },

  async getAllTasks(filters?: TaskFilters): Promise<Task[]> {
    try {
      // Build query string from filters
      const queryParams = new URLSearchParams();
      
      if (filters?.employee_id && filters.employee_id.length > 0) {
        filters.employee_id.forEach(id => {
          queryParams.append('employee_id', id.toString());
        });
      }
      
      if (filters?.date && filters.date.length > 0) {
        filters.date.forEach(date => {
          queryParams.append('date', date);
        });
      }
      
      if (filters?.include_deleted !== undefined) {
        queryParams.append('include_deleted', filters.include_deleted.toString());
      }
      
      const queryString = queryParams.toString();
      const url = `${MAIN_BACKEND_URL}/api/sql/tasks/${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch tasks' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  async updateTask(taskId: number, taskData: TaskUpdate): Promise<TaskUpdateResponse> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update task' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  async deleteTask(taskId: number): Promise<void> {
    try {
      const response = await fetch(`${MAIN_BACKEND_URL}/api/sql/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Task not found or already deleted');
        }
        const errorData = await response.json().catch(() => ({ detail: 'Failed to delete task' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // 204 No Content - successful deletion
      return;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },
};

